import {TradeType} from "./TradeType";
import {IsoDate} from "../../pseudo-types/DateTypes";
import {ExercisedPositionData, HistoricalEquityTradeData, HistoricalOptionTradeData} from "./AccountPerformanceSummary";
import {OrderSide} from "../../orders/models/OrderResponse";

export type HistoricalTrades = ReadonlyArray<HistoricalTradeData>;

export interface HistoricalTradeData {
    readonly tradeType: TradeType;
    readonly underlying: string;
    readonly code: string;
    readonly description: string;
    readonly activity: string;
    readonly side: OrderSide;
    readonly quantity: number;
    readonly cvf: number | null;
    readonly price: number;
    readonly tradeDate: IsoDate;
    readonly brokerage: number;
    readonly brokerageTax: number;
    readonly fees: number;
    readonly feesTax: number;
    readonly value: number;
    readonly nettValue: number;
    readonly totalFees: number;
    readonly totalBrokerage: number;
    readonly profitAndLossType: ProfitAndLossType;
}

export enum ProfitAndLossType {
    EQUITY_TRADE,
    OPTION_TRADE,
    CASH_SETTLED
}

export type HistoricalOptionTrades = ReadonlyArray<HistoricalOptionTrade>;

export class HistoricalOptionTrade implements HistoricalTradeData {

    private constructor(public readonly data: HistoricalOptionTradeData) {

    }

    public get tradeType(): TradeType {
        return TradeType.OPTION;
    }

    public get code(): string {
        return this.data.code;
    }

    public get underlying(): string {
        return this.data.underlying;
    }

    public get description(): string {
        return this.data.description;
    }

    public get activity(): string {
        return this.data.activity;
    }

    public get side(): OrderSide {
        return this.data.side;
    }

    public get quantity(): number {
        return this.data.quantity;
    }

    public get cvf(): number | null {
        return this.data.cvf;
    }

    public get price(): number {
        return this.data.price;
    }

    public get tradeDate(): IsoDate {
        return this.data.tradeDate;
    }

    public get brokerage(): number {
        return this.data.brokerage;
    }

    public get brokerageTax(): number {
        return this.data.brokerageTax;
    }

    public get fees(): number {
        return this.data.fees;
    }

    public get feesTax(): number {
        return this.data.feesTax;
    }

    public get value(): number {
        return this.data.tradeValue;
    }

    public get nettValue(): number {
        return this.data.nettValue;
    }

    public get totalFees(): number {
        return this.fees + this.feesTax;
    }

    public get totalBrokerage(): number {
        return this.brokerage + this.brokerageTax;
    }

    public get profitAndLossType(): ProfitAndLossType {
        return ProfitAndLossType.OPTION_TRADE;
    }

    public get expiry(): IsoDate {
        return this.data.expiry;
    }

    public static of(data: HistoricalOptionTradeData): HistoricalOptionTrade {
        return new HistoricalOptionTrade(data);
    }
}

export function isHistoricalOptionTrade(data: HistoricalTradeData): data is HistoricalOptionTrade {
    return data.profitAndLossType === ProfitAndLossType.OPTION_TRADE;
}

export type HistoricalEquityTrades = ReadonlyArray<HistoricalEquityTrade>;

export class HistoricalEquityTrade implements HistoricalTradeData {

    private constructor(public readonly data: HistoricalEquityTradeData) {

    }

    public get tradeType(): TradeType {
        return TradeType.EQUITY;
    }

    public get code(): string {
        return this.data.code;
    }

    public get underlying(): string {
        return this.data.code;
    }

    public get description(): string {
        return this.data.description;
    }

    public get activity(): string {
        return this.data.side;
    }

    public get side(): OrderSide {
        return this.data.side;
    }

    public get quantity(): number {
        return this.data.quantity;
    }

    public get cvf(): null {
        return null;
    }

    public get price(): number {
        return this.data.price;
    }

    public get tradeDate(): IsoDate {
        return this.data.tradeDate;
    }

    public get brokerage(): number {
        return this.data.brokerage;
    }

    public get brokerageTax(): number {
        return this.data.brokerageTax;
    }

    public get fees(): number {
        return this.data.fees;
    }

    public get feesTax(): number {
        return this.data.feesTax;
    }

    public get value(): number {
        return this.data.tradeValue;
    }

    public get nettValue(): number {
        return this.data.nettValue;
    }

    public get totalFees(): number {
        return this.fees + this.feesTax;
    }

    public get totalBrokerage(): number {
        return this.brokerage + this.brokerageTax;
    }

    public get profitAndLossType(): ProfitAndLossType {
        return ProfitAndLossType.EQUITY_TRADE;
    }

    public static of(data: HistoricalEquityTradeData): HistoricalEquityTrade {
        return new HistoricalEquityTrade(data);
    }
}

export function isHistoricalEquityTrade(data: HistoricalTradeData): data is HistoricalEquityTrade {
    return data.profitAndLossType === ProfitAndLossType.EQUITY_TRADE;
}

export type HistoricalExerciseTrades = ReadonlyArray<HistoricalExerciseTrade>;

export class HistoricalExerciseTrade implements HistoricalTradeData {

    private constructor(public readonly data: ExercisedPositionData) {

    }

    public get tradeType(): TradeType {
        return TradeType.OPTION;
    }

    public get code(): string {
        return this.data.code;
    }

    public get underlying(): string {
        return this.data.underlying;
    }

    public get description(): string {
        return this.data.description;
    }

    public get activity(): string {
        return this.data.contractOutcome;
    }

    public get side(): OrderSide {
        return this.data.side;
    }

    public get quantity(): number {
        return this.data.quantity;
    }

    public get cvf(): number | null {
        return this.data.cvf;
    }

    public get price(): number {
        return this.data.price;
    }

    public get tradeDate(): IsoDate {
        return this.data.asAt;
    }

    public get brokerage(): number {
        return 0;
    }

    public get brokerageTax(): number {
        return 0;
    }

    public get fees(): number {
        return this.data.fees;
    }

    public get feesTax(): number {
        return this.data.feesTax;
    }

    public get value(): number {
        return this.data.settlementCash;
    }

    public get nettValue(): number {
        return this.data.nettValue;
    }

    public get totalFees(): number {
        return this.fees + this.feesTax;
    }

    public get totalBrokerage(): number {
        return this.brokerage + this.brokerageTax;
    }

    public get profitAndLossType(): ProfitAndLossType {
        return ProfitAndLossType.CASH_SETTLED;
    }

    public static of(data: ExercisedPositionData): HistoricalExerciseTrade {
        return new HistoricalExerciseTrade(data);
    }
}

export function isHistoricalExerciseTrade(data: HistoricalTradeData): data is HistoricalExerciseTrade {
    return data.profitAndLossType === ProfitAndLossType.CASH_SETTLED;
}
